import React, { useCallback, useEffect, useState } from 'react';
import {
  AdminUserPermissions,
  fetchAdminUserPermissions,
  formatAdminUserLabel,
  updateAdminUserPermissions,
} from '../../../services/adminUserPermissionsService';
import {
  AdminBreadcrumbsTw,
  AdminDevTh,
  AdminEmpty,
  AdminPageHeader,
  AdminTableWrap,
  btnSecondary,
  filtersRowClass,
  inputClass,
  tdClass,
} from './adminUi';

type DraftPermissions = {
  canEditRecords: boolean;
  canGradeRecords: boolean;
};

function PermissionCheckbox({
  checked,
  disabled,
  onChange,
  label,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
  label: string;
}) {
  return (
    <label className={`inline-flex items-center gap-2 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        className="rounded border-slate-300"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
      />
      <span className="text-sm text-slate-700">{checked ? 'Да' : 'Нет'}</span>
    </label>
  );
}

export default function AdminUserPermissionsSection() {
  const [users, setUsers] = useState<AdminUserPermissions[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, DraftPermissions>>({});
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const res = await fetchAdminUserPermissions({
        search: search.trim() || undefined,
        page,
        limit: 25,
      });
      setUsers(res.data);
      setTotal(res.total);
      setDrafts(Object.fromEntries(
        res.data.map((user) => [user.id, {
          canEditRecords: user.canEditRecords,
          canGradeRecords: user.canGradeRecords,
        }]),
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить пользователей');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const updateDraft = (userId: string, patch: Partial<DraftPermissions>) => {
    setDrafts((prev) => ({
      ...prev,
      [userId]: { ...prev[userId], ...patch },
    }));
  };

  const saveUser = async (user: AdminUserPermissions) => {
    const draft = drafts[user.id];
    if (!draft) return;
    setSavingUserId(user.id);
    setError(null);
    try {
      const updated = await updateAdminUserPermissions(user.id, draft);
      setUsers((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
      setDrafts((prev) => ({
        ...prev,
        [updated.id]: {
          canEditRecords: updated.canEditRecords,
          canGradeRecords: updated.canGradeRecords,
        },
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить права');
    } finally {
      setSavingUserId(null);
    }
  };

  const isDirty = (user: AdminUserPermissions) => {
    const draft = drafts[user.id];
    if (!draft) return false;
    return draft.canEditRecords !== user.canEditRecords || draft.canGradeRecords !== user.canGradeRecords;
  };

  if (loading) return <AdminEmpty>Загрузка…</AdminEmpty>;

  return (
    <>
      <AdminBreadcrumbsTw items={[{ label: 'Админ-панель' }, { label: 'Права доступа' }]} />
      <AdminPageHeader
        title="Права доступа"
        subtitle="Выдайте сотрудникам доступ к редактированию записей о людях и грейдированию записей в админ-панели"
      />

      <div className={filtersRowClass}>
        <input
          className={inputClass}
          placeholder="Поиск по email, имени или логину"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
      </div>

      {error && (
        <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
      )}

      <AdminTableWrap refreshing={refreshing}>
        <table className="w-full min-w-[920px] text-sm">
          <thead className="border-b border-slate-100 bg-slate-50/80">
            <tr>
              <AdminDevTh label="Пользователь" dbField="app_users.email" />
              <AdminDevTh label="Редактирование записей" dbField="auth_roles.code=records_editor" />
              <AdminDevTh label="Грейдирование записей" dbField="auth_roles.code=records_grader" />
              <AdminDevTh label="Действия" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((user) => {
              const draft = drafts[user.id] ?? {
                canEditRecords: user.canEditRecords,
                canGradeRecords: user.canGradeRecords,
              };
              const dirty = isDirty(user);
              const disabled = user.isAdmin || savingUserId === user.id;

              return (
                <tr key={user.id} className="hover:bg-slate-50/50">
                  <td className={tdClass}>
                    <div className="font-medium text-slate-900">{formatAdminUserLabel(user)}</div>
                    <div className="text-xs text-slate-500">{user.email}</div>
                    {user.isAdmin && (
                      <div className="mt-1 text-xs font-medium text-dc-primary">Администратор — полный доступ</div>
                    )}
                    {!user.isAdmin && user.isAnalystLegacy && (
                      <div className="mt-1 text-xs text-amber-700">Ранее: роль analyst (обновится при сохранении)</div>
                    )}
                  </td>
                  <td className={tdClass}>
                    {user.isAdmin ? (
                      <span className="text-slate-600">Да</span>
                    ) : (
                      <PermissionCheckbox
                        label="Редактирование записей"
                        checked={draft.canEditRecords}
                        disabled={disabled}
                        onChange={(value) => updateDraft(user.id, { canEditRecords: value })}
                      />
                    )}
                  </td>
                  <td className={tdClass}>
                    {user.isAdmin ? (
                      <span className="text-slate-600">Да</span>
                    ) : (
                      <PermissionCheckbox
                        label="Грейдирование записей"
                        checked={draft.canGradeRecords}
                        disabled={disabled}
                        onChange={(value) => updateDraft(user.id, { canGradeRecords: value })}
                      />
                    )}
                  </td>
                  <td className={`${tdClass} whitespace-nowrap`}>
                    {!user.isAdmin && (
                      <button
                        type="button"
                        className={btnSecondary}
                        disabled={!dirty || savingUserId === user.id}
                        onClick={() => saveUser(user)}
                      >
                        {savingUserId === user.id ? 'Сохранение…' : 'Сохранить'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </AdminTableWrap>

      {users.length === 0 && (
        <p className="mt-4 text-sm text-slate-500">Пользователи не найдены.</p>
      )}

      {total > 25 && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-sm text-dc-muted">
          <span>Всего: {total}</span>
          <div className="flex gap-2">
            <button type="button" className={btnSecondary} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Назад
            </button>
            <button
              type="button"
              className={btnSecondary}
              disabled={page * 25 >= total}
              onClick={() => setPage((p) => p + 1)}
            >
              Вперёд
            </button>
          </div>
        </div>
      )}
    </>
  );
}
